import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { UserCog, Plus, Edit2, Trash2, Phone, Building2, CreditCard, Save, KeyRound, ShieldCheck, ShieldX, ExternalLink } from "lucide-react";
import { Sidebar, default as BottomNav } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useConfirmDialog } from "@/components/ConfirmDialog";

const InstructorManagement = ({ user, onLogout }) => {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [instructors, setInstructors] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [form, setForm] = useState({
    name: "", mobile: "", address: "",
    bank_account_number: "", ifsc_code: "", upi_id: "",
    per_hour_rate: "",
    provision_login: false, temp_password: "ChangeMe@123", studio_assignments: [],
  });

  // Grant-login dialog
  const [grantingFor, setGrantingFor] = useState(null);
  const [grantForm, setGrantForm] = useState({ temp_password: "ChangeMe@123", studio_assignments: [] });

  const fetchData = async () => {
    try {
      const [iRes, sRes] = await Promise.all([api.get("/instructors"), api.get("/studios")]);
      setInstructors(iRes.data.instructors || []);
      setStudios(sRes.data.studios || []);
    } catch (error) {
      toast.error("Failed to load instructors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setForm({ name: "", mobile: "", address: "", bank_account_number: "", ifsc_code: "", upi_id: "", per_hour_rate: "", provision_login: false, temp_password: "ChangeMe@123", studio_assignments: [] });
    setEditingInstructor(null);
  };

  const openAddDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (inst) => {
    setEditingInstructor(inst);
    setForm({
      name: inst.name || "",
      mobile: inst.mobile || "",
      address: inst.address || "",
      bank_account_number: inst.bank_account_number || "",
      ifsc_code: inst.ifsc_code || "",
      upi_id: inst.upi_id || "",
      per_hour_rate: inst.per_hour_rate ? String(inst.per_hour_rate) : "",
      provision_login: false, // Only relevant on create
      temp_password: "ChangeMe@123",
      studio_assignments: [],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    try {
      const payload = { ...form, per_hour_rate: parseFloat(form.per_hour_rate || 0) || 0 };
      if (editingInstructor) {
        await api.put(`/instructors/${editingInstructor.instructor_id}`, payload);
        toast.success("Instructor updated");
      } else {
        const res = await api.post("/instructors", payload);
        toast.success(form.provision_login ? "Instructor created with login access" : "Instructor created");
        if (form.provision_login && form.temp_password) {
          toast.info(`Temporary password: ${form.temp_password}`, { duration: 8000 });
        }
        void res;
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save instructor");
    }
  };

  const handleDelete = async (instructor_id) => {
    const ok = await confirm({
      title: "Delete this instructor?",
      message: "Their login (if any) is preserved in User Access.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/instructors/${instructor_id}`);
      toast.success("Instructor deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openGrantLogin = (inst) => {
    setGrantingFor(inst);
    setGrantForm({ temp_password: "ChangeMe@123", studio_assignments: [] });
  };

  const submitGrantLogin = async () => {
    try {
      const res = await api.post(`/instructors/${grantingFor.instructor_id}/grant-login`, grantForm);
      const msg = res.data?.message || "Login granted";
      toast.success(msg, { duration: 8000 });
      if (res.data?.temp_password) {
        toast.info(`Temporary password: ${res.data.temp_password}. Share with instructor.`, { duration: 10000 });
      }
      setGrantingFor(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant login");
    }
  };

  const revokeLogin = async (inst) => {
    const ok = await confirm({
      title: "Revoke login access?",
      message: `${inst.name} will no longer be able to sign in. Their instructor record and payroll history are preserved — you can re-grant access at any time.`,
      confirmText: "Revoke",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.post(`/instructors/${inst.instructor_id}/revoke-login`);
      toast.success("Login access revoked");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke");
    }
  };

  const reactivateLogin = async (inst) => {
    try {
      await api.post(`/instructors/${inst.instructor_id}/grant-login`, { temp_password: "ChangeMe@123", studio_assignments: inst.login_studio_assignments || [] });
      toast.success("Login reactivated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate");
    }
  };

  const toggleStudio = (formObj, setter, sid) => {
    const cur = formObj.studio_assignments || [];
    setter({ ...formObj, studio_assignments: cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid] });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pb-24 md:pb-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 12%, white)" }}>
                  <UserCog className="w-6 h-6" style={{ color: "var(--brand-primary)" }} />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Instructors</h1>
                  <p className="text-sm text-slate-500 mt-0.5">Manage teaching profiles, payroll, and login access.</p>
                </div>
              </div>
              <Button data-testid="add-instructor-btn" onClick={openAddDialog} className="rounded-full text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
                <Plus className="w-5 h-5 mr-2" /> Add Instructor
              </Button>
            </div>
          </motion.div>

          <div className="bg-violet-50/60 border border-violet-200/60 rounded-xl p-3 text-xs text-slate-600 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--brand-primary)" }} />
            <p>
              An instructor record holds the teaching & payroll profile. <strong>Login Access</strong> is optional — useful for guest/sub instructors who get paid but don't need to log in.
              Login holders can also be managed in <Link to="/users" className="font-medium underline" style={{ color: "var(--brand-primary)" }}>User Access</Link>.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : instructors.length === 0 ? (
            <div className="text-center py-16">
              <UserCog className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No instructors yet</h3>
              <p className="text-slate-400 mt-1">Add your first instructor to get started</p>
              <Button onClick={openAddDialog} className="mt-4 rounded-full text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
                <Plus className="w-4 h-4 mr-2" /> Add Instructor
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {instructors.map((inst, idx) => (
                <motion.div
                  key={inst.instructor_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  data-testid={`instructor-card-${inst.instructor_id}`}
                  className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">{inst.name}</h3>
                        {inst.has_login ? (
                          inst.login_active ? (
                            <span data-testid="login-badge-active" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <ShieldCheck className="w-3 h-3" /> Login Active
                            </span>
                          ) : (
                            <span data-testid="login-badge-inactive" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">
                              <ShieldX className="w-3 h-3" /> Login Revoked
                            </span>
                          )
                        ) : (
                          <span data-testid="login-badge-none" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            <ShieldX className="w-3 h-3" /> No Login
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-sm text-slate-500">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{inst.mobile}</span>
                      </div>
                      {inst.address && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{inst.address}</span>
                        </div>
                      )}
                      {(inst.bank_account_number || inst.upi_id) && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                          <CreditCard className="w-3.5 h-3.5 flex-shrink-0" />
                          {inst.upi_id && <span>UPI: {inst.upi_id}</span>}
                          {inst.bank_account_number && <span>A/C: ****{inst.bank_account_number.slice(-4)}</span>}
                        </div>
                      )}
                      {inst.per_hour_rate ? (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
                          ₹{inst.per_hour_rate}/hr
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(inst)} className="h-8 w-8" title="Edit profile">
                        <Edit2 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(inst.instructor_id)} className="h-8 w-8" title="Delete instructor">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>

                  {/* Login access actions */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                    {!inst.has_login && (
                      <Button
                        data-testid={`grant-login-${inst.instructor_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => openGrantLogin(inst)}
                        className="rounded-full"
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Grant Login
                      </Button>
                    )}
                    {inst.has_login && inst.login_active && (
                      <>
                        <Link
                          to="/users"
                          data-testid={`manage-login-${inst.instructor_id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Manage in User Access
                        </Link>
                        <Button
                          data-testid={`revoke-login-${inst.instructor_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => revokeLogin(inst)}
                          className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                        >
                          <ShieldX className="w-3.5 h-3.5 mr-1.5" /> Revoke
                        </Button>
                      </>
                    )}
                    {inst.has_login && !inst.login_active && (
                      <Button
                        data-testid={`reactivate-login-${inst.instructor_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => reactivateLogin(inst)}
                        className="rounded-full text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Reactivate
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
      <BottomNav user={user} onLogout={onLogout} />
      {confirmDialog}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInstructor ? "Edit Instructor" : "Add Instructor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input data-testid="instructor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input data-testid="instructor-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="h-11 rounded-xl" />
            </div>

            <div className="space-y-2">
              <Label>Per Hour Rate (₹) <span className="text-xs text-slate-400">(used for payroll)</span></Label>
              <Input
                data-testid="instructor-rate-input"
                type="number"
                min="0"
                value={form.per_hour_rate}
                onChange={(e) => setForm({ ...form, per_hour_rate: e.target.value })}
                placeholder="e.g. 500"
                className="h-11 rounded-xl"
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Banking Details (Optional)</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Bank Account Number</Label>
                  <Input data-testid="instructor-bank-input" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} placeholder="Account number" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })} placeholder="e.g. SBIN0001234" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>UPI ID / Number</Label>
                  <Input data-testid="instructor-upi-input" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="e.g. name@upi" className="h-11 rounded-xl" />
                </div>
              </div>
            </div>

            {!editingInstructor && (
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="provision-login-checkbox"
                    type="checkbox"
                    checked={form.provision_login}
                    onChange={(e) => setForm({ ...form, provision_login: e.target.checked })}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">Grant login access</span>
                    <p className="text-xs text-slate-500">Creates a User Access account so this instructor can log in.</p>
                  </div>
                </label>
                {form.provision_login && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Temporary Password</Label>
                      <Input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="h-10 rounded-xl" />
                      <p className="text-xs text-slate-500">User must change on first login.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Studio Access</Label>
                      <div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-xl p-2">
                        {studios.map((s) => (
                          <label key={s.studio_id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                            <input
                              type="checkbox"
                              checked={(form.studio_assignments || []).includes(s.studio_id)}
                              onChange={() => toggleStudio(form, setForm, s.studio_id)}
                            />
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            <span>{s.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button data-testid="save-instructor-btn" onClick={handleSave} className="w-full h-12 rounded-full text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Save className="w-5 h-5 mr-2" /> {editingInstructor ? "Update Instructor" : "Add Instructor"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grant Login Dialog */}
      <Dialog open={!!grantingFor} onOpenChange={(o) => { if (!o) setGrantingFor(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Grant Login Access</DialogTitle>
          </DialogHeader>
          {grantingFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Create a login account for <strong>{grantingFor.name}</strong> ({grantingFor.mobile}).
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Temporary Password</Label>
                <Input data-testid="grant-temp-password" value={grantForm.temp_password} onChange={(e) => setGrantForm({ ...grantForm, temp_password: e.target.value })} className="h-10 rounded-xl" />
                <p className="text-xs text-slate-500">Instructor must change on first login.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Studio Access</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-xl p-2">
                  {studios.length === 0 && <p className="text-xs text-slate-500">No studios available</p>}
                  {studios.map((s) => (
                    <label key={s.studio_id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                      <input
                        data-testid={`grant-studio-${s.studio_id}`}
                        type="checkbox"
                        checked={(grantForm.studio_assignments || []).includes(s.studio_id)}
                        onChange={() => toggleStudio(grantForm, setGrantForm, s.studio_id)}
                      />
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span>{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantingFor(null)}>Cancel</Button>
            <Button data-testid="grant-confirm" onClick={submitGrantLogin} className="text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
              <KeyRound className="w-4 h-4 mr-1.5" /> Grant Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstructorManagement;
